import React, { useCallback, useEffect, useRef, useState } from "react";
import CurrencyInput from "react-currency-input-field";

type Props = {
  id: string;
  value?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  prefix?: string;
  decimalsLimit?: number;
  decimalScale?: number;
  allowNegativeValue?: boolean;
  maxLength?: number;
  onCommit: (valueText: string) => void;
  style?: React.CSSProperties;
};

export default function DebouncedCurrencyInput({
  id,
  value = "",
  className,
  disabled,
  placeholder = "$",
  prefix = "$",
  decimalsLimit = 2,
  decimalScale = 2,
  allowNegativeValue = false,
  maxLength = 9,
  onCommit,
  style,
}: Props) {
  const [localValue, setLocalValue] = useState<string>(value ?? "");
  const timerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    // Keep local in sync when the upstream value changes externally
    if ((value ?? "") !== localValue) {
      setLocalValue(value ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const scheduleCommit = useCallback(
    (next: string) => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(() => {
        onCommit(next ?? "");
      }, 200); // 200ms debounce
    },
    [onCommit]
  );

  const handleValueChange = useCallback(
    (val: string | undefined) => {
      const next = val ?? "";
      setLocalValue(next);
      scheduleCommit(next);
    },
    [scheduleCommit]
  );

  const handleBlur = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    onCommit(localValue ?? "");
  }, [localValue, onCommit]);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <CurrencyInput
      id={id}
      placeholder={placeholder}
      prefix={prefix}
      decimalsLimit={decimalsLimit}
      decimalScale={decimalScale}
      allowNegativeValue={allowNegativeValue}
      maxLength={maxLength}
      value={localValue}
      disabled={disabled}
      className={className}
      onValueChange={(v) => handleValueChange(v)}
      onBlur={handleBlur}
      style={style}
    />
  );
}
